import { useElementSize } from '@mantine/hooks';
import { IconGripVertical } from '@tabler/icons-react';
import { ExpandedState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { AnalysisCommand, Order } from '../../data-access/types/orders';

import { TimelineTable } from './timeline-table';
import { TimelineView } from './timeline-view';
import classes from './timeline.module.css';
import { useTimelineScroll } from './use-timeline-scroll';
import { generateWeekHeaders, getWeekOffset } from './utils';

// Initialize dayjs plugins
dayjs.extend(isoWeek);

interface TimelineProps {
  pages: number;
  page: number;
  onPageChange: (page: number) => void;
  orders: Order[];
  earliestDate?: Date;
  latestDate?: Date;
}

export function Timeline({
  pages,
  page,
  onPageChange,
  orders,
  earliestDate,
  latestDate,
}: TimelineProps) {
  const [expandedState, setExpandedState] = useState<ExpandedState>({});
  const { ref: footerRef, height: footerHeight } = useElementSize();

  const minDate = dayjs(earliestDate)
    .subtract(7, 'day')
    .startOf('isoWeek')
    .toDate();
  const maxDate = dayjs(latestDate).add(7, 'day').endOf('isoWeek').toDate();

  // Generate week headers instead of day headers
  const weekHeaders = generateWeekHeaders(minDate, maxDate);
  const totalWeeks = weekHeaders.length;

  const currentDate = new Date();
  const currentWeekOffset = getWeekOffset(currentDate, minDate);

  const {
    headerScrollRef,
    leftScrollRef,
    rightScrollRef,
    handleLeftTableScroll,
    handleVerticalScroll,
    scrollToOrder,
  } = useTimelineScroll({ minDate, currentDateOffset: currentWeekOffset });

  const handleScrollToOrder = (order: Order) => {
    scrollToOrder(order);
  };

  const handleScrollToAnalysisCommand = (command: AnalysisCommand) => {
    // For analysis commands, we can scroll to their parent order
    // or implement specific scrolling logic if needed
    const parentOrder = orders.find((order) =>
      order.analysis_commands.some((cmd) => cmd.id === command.id),
    );
    if (parentOrder) {
      scrollToOrder(parentOrder);
    }
  };

  return (
    <PanelGroup className={classes.container} direction="horizontal">
      <Panel collapsedSize={3} collapsible defaultSize={30} minSize={20}>
        <TimelineTable
          expandedState={expandedState}
          footerRef={footerRef}
          leftScrollRef={leftScrollRef}
          onExpandedStateChange={setExpandedState}
          onLeftTableScroll={handleLeftTableScroll}
          onPageChange={onPageChange}
          onScrollToAnalysisCommand={handleScrollToAnalysisCommand}
          onScrollToOrder={handleScrollToOrder}
          orders={orders}
          page={page}
          pages={pages}
        />
      </Panel>
      <PanelResizeHandle className={classes.resizeHandle}>
        <div className={`${classes.resizeHandle}`}>
          <IconGripVertical stroke={1.5} />
        </div>
      </PanelResizeHandle>
      <Panel minSize={20}>
        <TimelineView
          currentWeekOffset={currentWeekOffset}
          expandedState={expandedState}
          footerHeight={footerHeight}
          headerScrollRef={headerScrollRef}
          minDate={minDate}
          onVerticalScroll={handleVerticalScroll}
          orders={orders}
          rightScrollRef={rightScrollRef}
          totalWeeks={totalWeeks}
          weekHeaders={weekHeaders}
        />
      </Panel>
    </PanelGroup>
  );
}
